import {useEffect} from "react";

import {useQuery} from "@tanstack/react-query";
import {onSnapshot} from "firebase/firestore";
import type {ZodType, z} from "zod/v4";

import {BaseCollectionConfig} from "~/api/BaseCollectionConfig";
import {queryClient} from "~/api/queryClient";
import {QueryOptions, buildQuery} from "~/api/queryUtils";

export const queryRealtimeHookFactory = <
  T extends ZodType,
  CollectionArgs extends NonNullable<unknown>,
>(
  config: BaseCollectionConfig<T, CollectionArgs>,
) => {
  return ({
    key,
    ...options
  }: {key: CollectionArgs} & QueryOptions<z.infer<T>>) => {
    useEffect(() => {
      const q = buildQuery(config.getCollectionPath(key), options);

      // Set up the Firestore onSnapshot listener
      const unsubscribe = onSnapshot(
        q,
        {
          includeMetadataChanges: true,
        },
        (querySnapshot) => {
          if (querySnapshot.metadata.hasPendingWrites) return;
          try {
            const data = querySnapshot.docs.map((doc) =>
              config.parseData(doc.id, doc.data()),
            );
            queryClient.setQueryData(
              [...config.getCollectionPath(key), JSON.stringify(options)],
              data,
            );
          } catch (err) {
            console.error(
              "Error handling query snapshot: ",
              err,
              querySnapshot,
            );
            throw err;
          }
        },
        (error) => {
          console.error("Error listening to collection: ", error);
        },
      );

      // Cleanup listener on unmount or when dependencies change
      return unsubscribe;
    }, [JSON.stringify(key), JSON.stringify(options)]);

    // Use React Query to manage fetching and cache invalidation
    return useQuery<z.infer<T>[]>({
      queryKey: [...config.getCollectionPath(key), options],
      queryFn: () => {
        // Immediately return cached data if available due to listener updates
        return (
          queryClient.getQueryData<z.infer<T>[]>([
            ...config.getCollectionPath(key),
            options,
          ]) || []
        );
      },
      enabled: !Object.values(key).some((segment) => !segment),
      staleTime: Number.POSITIVE_INFINITY, // Data stays fresh due to real-time updates
    });
  };
};
